import { useState } from "react";
import axios from "../lib/api";
import { User, Building2, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const Profile = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    company_name: user?.company_name || ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/user/profile`,
        formData,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: formData
        }
      );
      setUser({ ...user, ...formData });
      toast.success("Perfil actualizado correctamente");
    } catch (error) {
      toast.error("Error al actualizar el perfil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl" data-testid="profile-page">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Mi Perfil</h1>
          <p className="text-slate-600">Gestiona tu información personal</p>
        </div>

        {/* User Info Card */}
        <Card className="bg-white border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-900">Información de la cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-red-600" />
                </div>
              )}
              <div>
                <p className="font-bold text-slate-900 text-lg">{user?.name}</p>
                <p className="text-slate-500">{user?.email}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Nombre</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-700">Empresa</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="company"
                    type="text"
                    className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                    placeholder="Nombre de tu empresa"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    data-testid="profile-company-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white btn-press"
                disabled={loading}
                data-testid="save-profile-btn"
              >
                {loading ? (
                  "Guardando..."
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Stats */}
        <Card className="bg-slate-50 border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-900">Resumen de tu cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <p className="label-swiss">Plan actual</p>
                <p className="text-xl font-bold text-slate-900 mt-1">Free</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <p className="label-swiss">Miembro desde</p>
                <p className="text-xl font-bold text-slate-900 mt-1">Ene 2025</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
